<template>
  <section
    class="pb-3"
  >
    <b-row
      v-if="loading"
      class="d-flex justify-content-between mb-1 px-25"
    >
      <b-col
        sm="3"
        class="d-flex justify-content-between"
      >
        <b-skeleton
          type="button"
          width="70%"
          class="mb-0"
          height="36px"
        />
      </b-col>
      <b-col
        sm="9"
        class="d-flex justify-content-end"
      >
        <b-skeleton type="button" />
      </b-col>
    </b-row>
    <b-row
      v-else
      class="d-flex justify-content-between mb-2"
    >
      <b-col sm="5">
        <span class="mr-50">{{ $tk('ClientPortal.MasterTable.ShowDataPer') }}</span>
        <b-dropdown
          ref="dateRangePickerDropdown"
          menu-class="py-0"
          variant="outline-dark"
          class="bg-white ml-0 mb-50 mb-md-0"
          size="sm"
          no-caret
        >
          <template #button-content>
            {{ wrapper.filter.dateRange }}
            <GjIcon
              name="Calendar"
              class="ml-50"
            />
          </template>
          <div class="p-2">
            <date-range-picker
              ref="dateRangePicker"
              :ranges="predefinedRanges"
              default-range-select="defaultRange"
              @submit="submitDateRange"
              @cancel="closeDateRangePicker"
            />
          </div>
        </b-dropdown>
      </b-col>
      <b-col
        sm="7"
        class="d-flex justify-content-end"
      >
        <filter-wrapper
          :properties="selectedFilters"
          :width-offset="700"
          @clicked="openSelectedFilter"
          @cleared="clearFilters"
        />
        <dropdown
          :title="$tk('ClientPortal.MasterTable.Filters')"
          :show-dropdown="showFilters"
          :disabled="isLoading"
          @change="toggleFilters"
        >
          <template #menu-items>
            <dropdown-submenu
              v-for="(filter, index) in wrapper.propertyCatalogs.length ? [ 'Properties', 'Catalogs' ] : [ 'Properties' ]"
              :key="filter + '-' + index"
              :input-id="filter"
              :text="filter"
              input-name="submenu"
              :selected-filter="selectedFilter(filter)"
              :checked="menu"
              :submenus="filter === 'Catalogs' ? true : false"
              @changeSelectedMenu="changeMenu"
            >
              <template #items>
                <dropdown-search-item
                  v-if="filter ==='Properties'"
                  :key="'search-'+filter"
                  :data="wrapper"
                />
                <template v-if="filter ==='Properties'">
                  <dropdown-item
                    v-for="(property, propertyIndex) in wrapper.properties.filter(option => option.name.toLowerCase().startsWith(wrapper.propertySearch.toLowerCase()))"
                    :key="property.name + '-' + propertyIndex"
                    :label="property.name"
                    :filter-name="filter"
                    :value="property.id"
                    :menu-name="filter"
                    :selected="property.selected"
                    @change="addFilterPropertyIds(property.id)"
                  />
                </template>
                <template v-else-if="filter==='Catalogs'">
                  <dropdown-submenu
                    v-for="(propertyCatalog, propertyCatalogIndex) in wrapper.propertyCatalogs"
                    :key="propertyCatalog.name + '-' + propertyCatalogIndex"
                    :input-id="propertyCatalog.name"
                    :text="propertyCatalog.name"
                    input-name="submenu-1"
                    :contains-images="true"
                    :checked="submenu"
                    @changeSelectedMenu="changeSubmenu(propertyCatalog.name); searchMonetizationContentItems(propertyCatalog)"
                  >
                    <template #items>
                      <dropdown-search-item
                        :key="'search-'+filter"
                        :data="propertyCatalog"
                        @searchChange="searchMonetizationContentItems(propertyCatalog)"
                      />
                      <dropdown-item
                        v-if="showAvailableItems"
                      >
                        {{ $tk('ClientPortal.Filters.NoAvailableItems') }}
                      </dropdown-item>
                      <dropdown-item
                        v-for="(catalog, catalogIndex) in propertyCatalog.availableItems"
                        :key="catalog.contentName + '-' + catalogIndex"
                        :label="catalog.contentName"
                        :filter-name="filter"
                        :value="catalog.id.toString()"
                        :menu-name="filter"
                        :image="catalog.contentImageUrl"
                        :selected="catalog.include"
                        @change="saveMonetizationContentFilter(catalog)"
                      />
                    </template>
                    <template #footer>
                      <dropdown-clear-item
                        :title="$tk('ClientPortal.Filters.Clear')"
                        @click="removeSelectedMonetizationContentFilter(propertyCatalog)"
                      />
                    </template>
                  </dropdown-submenu>
                </template>
              </template>
              <template #footer>
                <dropdown-clear-item
                  :title="$tk('ClientPortal.Filters.Clear')"
                  @click="clearFilters(filter, false)"
                />
              </template>
            </dropdown-submenu>
            <dropdown-clear-item
              :title="$tk('ClientPortal.Filters.ClearAll')"
              @click="clearAllFilters"
            />
          </template>
        </dropdown>
      </b-col>
    </b-row>
    <div v-if="loading">
      <earning-skeleton />
    </div>
    <div v-else>
      <h6>{{ $tk('ClientPortal.Property.Earnings.EstimatedEarnings') }}</h6>
      <EstimatedEarnings
        :earning-stats="earningStats"
        :loading-earning-stats="loadingEarningStats"
      />
      <b-row class="mb-50">
        <b-col>
          <h6>{{ $tk('ClientPortal.Property.Earnings.Statistics') }}</h6>
        </b-col>
        <b-col class="text-right">
          <EarningsExport
            :date-range="wrapper.filter.dateRange"
            :property-ids="wrapper.filter.propertyIds"
            :monetization-content-ids="wrapper.filter.monetizationContentIds"
          />
        </b-col>
      </b-row>
      <EarningStatistics
        :summary-stats="summaryStats"
        :loading-summary-stats="loadingSummaryStats"
        :compare="wrapper.filter.compare"
      />
      <b-card class="mb-2">
        <template
          #header
        >
          <div class="d-flex flex-column flex-md-row justify-content-start justify-content-md-between w-100">
            <h5 class="mb-1 mb-md-0 mr-0">
              {{ $tk('ClientPortal.Property.Earnings.Graph') }}
            </h5>
            <div class="d-flex flex-column flex-md-row justify-content-start align-items-center">
              <span class="mr-50">{{ $tk('ClientPortal.Property.Earnings.ShowBy') }}</span>
              <b-dropdown
                class="mr-0 mr-md-50 mb-25 mb-md-0"
                variant="outline-dark"
                size="sm"
              >
                <template #button-content>
                  <i class="chart-dot-blue mr-50" />
                  {{ firstChartFilterLabelName }}
                </template>
                <b-dropdown-item
                  v-for="label in wrapper.chartLabels"
                  :key="label.name"
                  :disabled="secondChartFilterLabelName === label.name && !wrapper.filter.compare"
                  :active="firstChartFilterLabelName === label.name"
                  @click="setFirstChartLabel(label.value)"
                >
                  {{ label.name }}
                </b-dropdown-item>
              </b-dropdown>
              <b-dropdown
                v-if="!wrapper.filter.compare"
                class="mr-0 mr-md-1 mb-1 mb-md-0"
                variant="outline-dark"
                size="sm"
              >
                <template #button-content>
                  <i class="chart-dot-red mr-50" />
                  {{ secondChartFilterLabelName }}
                </template>
                <b-dropdown-item
                  v-for="label in wrapper.chartLabels"
                  :key="label.name"
                  :disabled="firstChartFilterLabelName === label.name && !wrapper.filter.compare"
                  :active="secondChartFilterLabelName === label.name"
                  @click="setSecondChartLabel(label.value)"
                >
                  {{ label.name }}
                </b-dropdown-item>
              </b-dropdown>
              <span class="mr-50 ml-2">{{ $tk('ClientPortal.Property.Earnings.TimePeriod') }}</span>
              <b-dropdown
                variant="outline-dark"
                size="sm"
                right
              >
                <template #button-content>
                  {{ wrapper.filter.chartInterval }}
                </template>
                <b-dropdown-item
                  v-for="interval in wrapper.chartIntervals"
                  :key="interval.name"
                  :active="wrapper.filter.chartInterval === interval.value"
                  @click="setChartInterval(interval.value)"
                >
                  {{ interval.name }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </template>
        <b-skeleton-img
          v-if="loadingChartStats"
          no-aspect
          height="400px"
        />
        <apexcharts
          v-if="!loadingChartStats"
          height="200"
          type="line"
          :options="chartOptions"
          :series="chartStats.series"
        />
      </b-card>
      <PlacementStatisticsTable
        :compare="wrapper.filter.compare"
        :placement-stats="placementStats"
        :loading-placement-stats="loadingPlacementStats"
      />
    </div>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import VueApexCharts from 'vue-apexcharts'
import DateRangePicker from '@/components/VueDateRangePicker'
import moment from 'moment'
import PropertyStatisticsService from '@/api/client/propertyStatistics.service'
import CatalogsService from '@/api/client/catalogs.service'
import notification from '@/mixins/toastification'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownSearchItem from '@/components/DropdownSearchItem.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import FilterWrapper from '@/components/FilterWrapper.vue'
import Vue from 'vue'
import PlacementStatisticsTable from './PlacementStatisticsTable.vue'
import EarningStatistics from './EarningStatistics.vue'
import EarningsExport from './EarningsExport.vue'
import EstimatedEarnings from './EstimatedEarnings.vue'
import EarningSkeleton from './EarningsSkeleton.vue'

export default {
  components: {
    apexcharts: VueApexCharts,
    GjIcon,
    DateRangePicker,
    EarningsExport,
    EarningStatistics,
    EarningSkeleton,
    PlacementStatisticsTable,
    EstimatedEarnings,
    Dropdown,
    DropdownItem,
    DropdownSubmenu,
    FilterWrapper,
    DropdownClearItem,
    DropdownSearchItem,
  },
  mixins: [notification],
  data() {
    return {
      wrapper: {},
      loading: true,
      loadingEarningStats: true,
      loadingSummaryStats: true,
      loadingChartStats: true,
      loadingPlacementStats: true,
      loadingCatalogItems: false,
      earningStats: {},
      summaryStats: {},
      chartStats: {},
      placementStats: [],
      menu: '',
      submenu: '',
      showAvailableItems: false,
      showFilters: false,
      selectedFilters: [],
      predefinedRanges: {
        today: {
          label: this.$tk('ClientPortal.PredefinedRanges.Today'),
          startDate: moment(),
          endDate: moment(),
        },
        yesterday: {
          label: this.$tk('ClientPortal.PredefinedRanges.Yesterday'),
          startDate: moment().subtract(1, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        last7Days: {
          label: this.$tk('ClientPortal.PredefinedRanges.Last7Days'),
          startDate: moment().subtract(7, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        last30Days: {
          label: this.$tk('ClientPortal.PredefinedRanges.Last30Days'),
          startDate: moment().subtract(30, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        lastMonth: {
          label: this.$tk('ClientPortal.PredefinedRanges.LastMonth'),
          startDate: moment().subtract(1, 'months').startOf('month'),
          endDate: moment().subtract(1, 'month').endOf('month'),
        },
        defaultRange: {
          label: this.$tk('ClientPortal.PredefinedRanges.DefaultRange'),
          startDate: moment().subtract(7, 'days'),
          endDate: moment(),
        },
      },
    }
  },
  computed: {
    firstChartFilterLabelName() {
      var index = this.wrapper.chartLabels.findIndex(x => x.value === this.wrapper.filter.firstChartLabel)
      return this.wrapper.chartLabels[index].name
    },
    secondChartFilterLabelName() {
      var index = this.wrapper.chartLabels.findIndex(x => x.value === this.wrapper.filter.secondChartLabel)
      return this.wrapper.chartLabels[index].name
    },
    chartOptions() {
      const $this = this
      return {
        chart: {
          shadow: {
            enabled: true,
            color: '#000',
            top: 18,
            left: 7,
            blur: 10,
            opacity: 1,
          },
          toolbar: {
            show: false,
          },
        },
        colors: ['#1D79F2', '#E34850'],
        dataLabels: {
          enabled: true,
          formatter(val) {
            return val.toLocaleString()
          },
        },
        stroke: {
          curve: 'smooth',
        },
        grid: {
          borderColor: '#e7e7e7',
          row: {
            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
            opacity: 0.5,
          },
          padding: {
            left: 30,
            right: 0,
          },
        },
        markers: {
          size: 5,
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: $this.chartStats.xAxisLabels,
        },
        yaxis: {
          decimalsInFloat: 2,
          labels: {
            formatter(value) {
              return value.toLocaleString()
            },
            align: 'left',
            offsetX: -10,
          },
        },
        tooltip: {
          x: {
            formatter(index) {
              if ($this.wrapper.filter.compare) {
                return $this.firstChartFilterLabelName
              }
              return $this.chartStats.xAxisLabels[index - 1]
            },
          },
          y: {
            title: {
              formatter(seriesName, { dataPointIndex }) {
                if ($this.wrapper.filter.compare) {
                  if (seriesName === '1st') {
                    return $this.chartStats.series[0].tooltips[dataPointIndex].label
                  }
                  return $this.chartStats.series[1].tooltips[dataPointIndex].label
                }
                return seriesName
              },
            },
            formatter(value, { seriesIndex, dataPointIndex }) {
              return $this.chartStats.series[seriesIndex].tooltips[dataPointIndex].value
            },
          },
          z: {
            title: 'Title',
          },
        },

      }
    },
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    document.addEventListener('click', this.onClick)
    this.loading = true
    try {
      this.wrapper = await PropertyStatisticsService.getEarningsDashboard(this.$route.params.accountId)
      await this.loadAllStatistics()
    } catch (err) {
      console.log(err)
      this.error(err)
    } finally {
      this.loading = false
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClick)
  },
  methods: {
    onClick(e) {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        this.toggleFilters(false)
        this.menu.value = ''
      }
    },
    async fetchData() {
      this.loading = true
      try {
        this.wrapper = await PropertyStatisticsService.getEarningsDashboard(this.$route.params.accountId)
        await this.loadAllStatistics()
      } catch (err) {
        console.log(err)
        this.error(err)
      } finally {
        this.loading = false
        this.selectedFilters = []
      }
    },
    submitDateRange(range) {
      // eslint-disable-next-line object-curly-newline
      const { startDate, endDate, startDateCompare, endDateCompare, compare } = range

      const firstRange = this.getDateFromMomentRange(startDate, endDate)

      this.wrapper.filter.compare = compare

      if (compare) {
        const secondRange = this.getDateFromMomentRange(startDateCompare, endDateCompare)
        this.wrapper.filter.dateRange = `${firstRange} & ${secondRange}`
      } else {
        this.wrapper.filter.dateRange = firstRange
      }

      this.loadMonetizationStatistics()
      this.closeDateRangePicker()
    },
    getDateFromMomentRange(startMoment, endMoment) {
      const startDate = new Date(startMoment)
      const endDate = new Date(endMoment)
      return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`
    },
    formatDate(date) {
      const day = this.prependZero(date.getDate())
      const month = this.prependZero(date.getMonth() + 1)
      const year = this.prependZero(date.getFullYear())
      return `${day}/${month}/${year}`
    },
    prependZero(value) {
      if (value.toString().length === 1) {
        return `0${value}`
      }

      return value
    },
    closeDateRangePicker() {
      this.$refs.dateRangePickerDropdown.hide(true)
    },
    removeSelectedProperty() {
      this.wrapper.properties.forEach(property => {
        property.selected = false
      })
      this.wrapper.filter.propertyIds = []
      this.menu = ''
      const index = this.selectedFilters.map(object => object.name).indexOf('Properties')
      if (index !== -1) {
        this.selectedFilters.splice(index, 1)
      }
      this.loadAllStatistics()
    },
    removeSelectedCatalog() {
      this.wrapper.filter.monetizationContentIds = []
      this.menu = ''
      this.submenu = ''
      const index = this.selectedFilters.map(object => object.name).indexOf('Catalogs')
      if (index !== -1) {
        this.selectedFilters.splice(index, 1)
      }
      this.loadAllStatistics()
    },
    addFilterPropertyIds(propertyId) {
      const selectedProperty = this.wrapper.properties.find(x => x.id === propertyId)
      selectedProperty.selected = !selectedProperty.selected
      const selectedProperties = this.wrapper.properties.filter(x => x.selected)
      this.wrapper.filter.propertyIds = selectedProperties.map(x => x.id)
      const index = this.selectedFilters.map(object => object.name).indexOf('Properties')
      if (index === -1) {
        this.selectedFilters.push({ name: 'Properties', selectedFilters: this.wrapper.filter.propertyIds.length })
      } else if (this.wrapper.filter.propertyIds.length) {
        this.selectedFilters[index].selectedFilters = this.wrapper.filter.propertyIds.length
      } else {
        this.selectedFilters.splice(index, 1)
      }
      this.loadAllStatistics()
    },
    async loadAllStatistics() {
      await Promise.all([
        this.loadEarningStats(),
        this.loadMonetizationStatistics(),
      ])
    },
    async loadMonetizationStatistics() {
      await Promise.all([
        this.loadSummaryStats(),
        this.loadChartStats(),
        this.loadPlacementStats(),
      ])
    },
    async loadEarningStats() {
      this.loadingEarningStats = true
      try {
        this.earningStats = await PropertyStatisticsService.getEarningStats(this.wrapper.filter, this.$route.params.accountId)
      } catch (err) {
        this.error(err)
      } finally {
        this.loadingEarningStats = false
      }
    },
    async loadSummaryStats() {
      this.loadingSummaryStats = true
      try {
        this.summaryStats = await PropertyStatisticsService.getSummaryStats(this.wrapper.filter, this.$route.params.accountId)
      } catch (err) {
        this.error(err)
      } finally {
        this.loadingSummaryStats = false
      }
    },
    async loadChartStats() {
      this.loadingChartStats = true
      try {
        this.chartStats = await PropertyStatisticsService.getChartStats(this.wrapper.filter, this.$route.params.accountId)
      } catch (err) {
        this.error(err)
      } finally {
        this.loadingChartStats = false
      }
    },
    async loadPlacementStats() {
      this.loadingPlacementStats = true
      try {
        this.placementStats = await PropertyStatisticsService.getPlacementStats(this.wrapper.filter, this.$route.params.accountId)
      } catch (err) {
        this.error(err)
      } finally {
        this.loadingPlacementStats = false
      }
    },
    async searchMonetizationContentItems(propertyCatalog) {
      Vue.set(propertyCatalog, 'availableItems', [])
      this.loadingCatalogItems = true
      try {
        propertyCatalog.availableItems = await CatalogsService.searchCatalogItems(propertyCatalog.id, propertyCatalog.search, this.$route.params.accountId)
        propertyCatalog.availableItems.forEach(x => {
          if (this.wrapper.filter.monetizationContentIds.indexOf(x.id) !== -1) {
            x.include = !x.include
          }
        })
      } catch (err) {
        this.error(err.message)
      } finally {
        this.loadingCatalogItems = false
      }
      if (propertyCatalog.availableItems.length) {
        this.showAvailableItems = false
      } else {
        this.showAvailableItems = true
      }
    },
    saveMonetizationContentFilter(catalog) {
      catalog.include = !catalog.include
      const indexOfContentIds = this.wrapper.filter.monetizationContentIds.indexOf(catalog.id)
      if (catalog.include && indexOfContentIds === -1) {
        this.wrapper.filter.monetizationContentIds.push(catalog.id)
      } else {
        this.wrapper.filter.monetizationContentIds.splice(indexOfContentIds, 1)
      }
      const index = this.selectedFilters.map(object => object.name).indexOf('Catalogs')
      if (index === -1) {
        this.selectedFilters.push({ name: 'Catalogs', selectedFilters: this.wrapper.filter.monetizationContentIds.length })
      } else if (this.wrapper.filter.monetizationContentIds.length) {
        this.selectedFilters[index].selectedFilters = this.wrapper.filter.monetizationContentIds.length
      } else {
        this.selectedFilters.splice(index, 1)
      }
      this.loadMonetizationStatistics()
    },

    removeSelectedMonetizationContentFilter(propertyCatalog) {
      propertyCatalog.availableItems.forEach(item => {
        item.include = false
      })
      this.wrapper.filter.monetizationContentIds = this.wrapper.filter.monetizationContentIds.filter(item => !propertyCatalog.availableItems.find(f => f.id === item))
      this.submenu = ''
      const index = this.selectedFilters.map(object => object.name).indexOf('Catalogs')
      if (index !== -1 && this.wrapper.filter.monetizationContentIds.length === 0) {
        this.selectedFilters.splice(index, 1)
      } else {
        this.selectedFilters[index].selectedFilters = this.wrapper.filter.monetizationContentIds.length
      }
      this.loadMonetizationStatistics()
    },
    setFirstChartLabel(chartLabel) {
      if (this.wrapper.filter.firstChartLabel === chartLabel) return

      this.wrapper.filter.firstChartLabel = chartLabel
      this.loadChartStats()
    },
    setSecondChartLabel(chartLabel) {
      if (this.wrapper.filter.secondChartLabel === chartLabel) return

      this.wrapper.filter.secondChartLabel = chartLabel
      this.loadChartStats()
    },
    setChartInterval(interval) {
      if (this.wrapper.filter.chartInterval === interval) return

      this.wrapper.filter.chartInterval = interval
      this.loadChartStats()
    },
    toggleFilters(toogleFilter = null) {
      this.menu = ''
      this.showFilters = toogleFilter ?? !this.showFilters
    },
    changeMenu(newOpenedMenu) {
      if (this.menu === newOpenedMenu) {
        this.menu = ''
      } else {
        this.menu = newOpenedMenu
        this.submenu = ''
      }
    },
    changeSubmenu(newOpenedSubmenu) {
      if (this.submenu === newOpenedSubmenu) { this.$set(this, 'submenu', '') } else { this.submenu = newOpenedSubmenu }
    },
    clearFilters(filterName, closeAllDropdowns = true) {
      if (filterName === 'Properties') {
        this.removeSelectedProperty()
      } else {
        this.removeSelectedCatalog()
      }
      if (closeAllDropdowns) { this.toggleFilters(false) }
    },
    clearAllFilters() {
      this.removeSelectedProperty()
      this.removeSelectedCatalog()
      this.toggleFilters(false)
    },
    selectedFilter(name) {
      const filter = this.selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    },
    openSelectedFilter(filterName) {
      this.toggleFilters(true)
      this.menu = filterName
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/views/client/properties/catalog/earnings/earnings.scss';
</style>
